<template>
  <q-dialog v-model="visible">
    <q-card style="width: 600px !important">
      <q-toolbar class="auto-height horizontal-line">
        <q-toolbar-title class="q-pa-sm">プレビュー</q-toolbar-title>
        <q-btn flat round dense icon="close" @click="onCancel" />
      </q-toolbar>
      <q-card-section class="q-pa-md horizontal-line">
        <CLoading :loading="loading" />
        <q-form ref="popupForm" greedy>
          <div class="row" v-if="form._id !== ''">
            <div class="q-mr-md text-grey-9 col-2 column items-end justify-center">
              <div>ID</div>
            </div>
            <q-input disable outlined v-model="form._id" dense :placeholder="$t('label.display_id')" class="col" />
          </div>
          <div class="row q-mt-sm">
            <div class="q-mr-md q-mb-md text-grey-9 col-2 column items-end justify-center">
              <div>{{ $t('label.popup.popup_name') }}</div>
            </div>
            <q-input
              lazy-rules
              :rules="[(val) => (val && val.length > 0) || requiredRule.popup_name]"
              outlined
              v-model="form.popup_name"
              dense
              :placeholder="$t('label.popup.popup_name')"
              class="col"
            />
          </div>
          <q-separator class="q-mb-md" />
          <div class="row">
            <div class="q-mr-sm">
              <q-avatar rounded size="120px" v-if="form.images.length" class="dashed-border">
                <img :src="form.images[0].pc.img_thumb" class="cursor-pointer" />
              </q-avatar>
              <q-avatar
                rounded
                size="120px"
                icon="image"
                color="grey-3"
                text-color="grey"
                v-else
                class="dashed-border"
              />
              <div class="error-message" v-if="errorImageMessage !== ''">
                <span class="text-red">{{ errorImageMessage }}</span>
              </div>
            </div>
            <div class="col">
              <div>
                <ul style="padding-left: 20px; margin-top: 4px">
                  <li>画像形式: jpeg / png / gif</li>
                  <li>
                    推奨サイズ:
                    <ul style="padding-left: 20px; margin-top: 4px">
                      <li>正方形: 1024px * 1024px (width * height = 1:1)</li>
                      <li>縦長: width (800 - 2500 px)/height (250px ~ 2500px)</li>
                    </ul>
                  </li>

                  <li>画像サイズ: 10MB</li>
                </ul>
              </div>
              <div class="q-mt-sm row" v-if="form.images">
                <div class="col q-mr-sm q-mb-sm">
                  <q-file
                    name="image_file"
                    :label="$t('label.common.messages.add_image')"
                    outlined
                    dense
                    accept=".jpg, image/*"
                    v-model="file"
                    bg-color="btn-upload"
                    label-color="btn-upload"
                    class="q-file-btn"
                  />
                </div>
                <div class="col">
                  <MediaUploader :light-style="true" :is-crop-image="false" @on-select-media="onSelectMedia" />
                </div>
              </div>
            </div>
          </div>
          <q-separator class="q-my-md" />
        </q-form>
        <div class="row no-wrap items-center q-mt-md">
          <q-space />
          <div class="q-gutter-sm">
            <q-btn no-caps class="btn-cancel" @click="onCancel">{{ $t('cancel') }} </q-btn>
            <q-btn no-caps class="btn-save" @click="onSubmit">
              {{ form._id !== '' ? $t('save') : $t('add_new') }}
            </q-btn>
          </div>
        </div>
      </q-card-section>
    </q-card>
  </q-dialog>
  <UploadingProgress :files="cropFiles" />
  <CropperImageSelector
    v-if="cropperImageModalVisible"
    :modalVisible="cropperImageModalVisible"
    :file="file"
    @update:closeModal="onCloseModal"
    @update:onCropImage="onCropImage"
    :aspectRatio="1.45"
  />
</template>

<script lang="ts">
import { mixins, Options } from 'vue-class-component'
import BaseFormDialogMixin from '@/components/common/mixins/BaseFormDialogMixin.vue'
import { Prop, Watch } from 'vue-property-decorator'
import MediaUploader from '@/components/media-manager/MediaUploader.vue'
import { IMedia, IPopup, IUploadFileResponse } from '@/utils/types'
import { constant } from '@/utils/constants'
import cloneDeep from 'lodash/cloneDeep'
import UploadingProgress from '@/components/common/ui/UploadingProgress.vue'
import UploadApi from '@/api/upload'
import CropperImageSelector from '@/components/tappable-area/selectors/CropperImageSelector.vue'
import CLoading from '@/components/common/ui/CLoading.vue'

@Options({
  components: { CLoading, CropperImageSelector, UploadingProgress, MediaUploader },
  emits: ['update:modelValue', 'update:addPopup', 'update:updatePopup'],
})
export default class PopupModal extends mixins(BaseFormDialogMixin) {
  @Prop()
  popupData!: IPopup

  form: IPopup = {
    _id: '',
    app_id: '',
    campaign_id: '',
    is_active: false,
    is_test_mode: false,
    display_id: '',
    popup_name: '',
    images: [],
    animation_type: constant.animation_type.fade_in,
  }

  errorImageMessage = ''
  file: File | null = null
  cropFiles: File[] = []
  cropperImageModalVisible = false
  loading = false

  get requiredRule() {
    return {
      popup_name: this.$t('validate.this_field_is_required', {
        placeholder: this.$t('label.popup.popup_name'),
      }),
      messageImage: this.$t('validate.this_field_is_required', {
        placeholder: this.$t('label.common.messages.image'),
      }),
    }
  }

  async onSelectMedia(file: IMedia) {
    this.errorImageMessage = ''
    if (file) {
      const imageSetting = {
        img_org: file.url ?? '',
        img_thumb: file.thumb ?? '',
        filename: file.name ?? '',
      }
      this.form.images = [
        {
          pc: imageSetting,
          sp: JSON.parse(JSON.stringify(imageSetting)),
        },
      ]
    }
  }

  onSubmit() {
    const isValid = this.validatePopup()
    this.$refs.popupForm
      .validate()
      .then(async (success: boolean) => {
        if (!success || !isValid) {
          return
        }
        if (this.isNew) {
          this.$emit('update:addPopup', this.form)
        } else {
          this.$emit('update:updatePopup', this.form)
        }
        this.visible = false
      })
      .catch((error: unknown) => {
        console.log('error', error)
      })
  }

  validatePopup() {
    this.errorImageMessage = ''
    if (!this.form.images || this.form.images.length === 0) {
      this.errorImageMessage = this.requiredRule.messageImage
      return false
    }

    return true
  }

  async validateInputFile(file: File) {
    this.errorImageMessage = ''

    if (!file && this.form.images.length === 0) {
      this.errorImageMessage = this.requiredRule.messageImage
      return false
    }

    if (file.size > constant.STORY_POPUP.MAX_FILE_SIZE) {
      this.errorImageMessage = this.$t('validate.file_too_big', {
        placeholder: file.name,
      })

      return false
    }

    let result = true
    if (file) {
      console.log(file)
      result = await this.validateImageSize(file)
    }

    return result
  }

  async validateImageSize(file: File) {
    return new Promise<boolean>((resolve) => {
      const img = new Image()
      img.src = window.URL.createObjectURL(file)
      img.onload = (event: Event) => {
        const data = event.target as HTMLImageElement

        if (data.width < constant.STORY_POPUP.MIN_IMAGE_WIDTH || data.width > constant.STORY_POPUP.MAX_IMAGE_WIDTH) {
          this.errorImageMessage = this.$t('validate.image_width_size', {
            placeholder: file.name,
          })
          resolve(false)
        }
        if (
          data.height < constant.STORY_POPUP.MIN_IMAGE_HEIGHT ||
          data.height > constant.STORY_POPUP.MAX_IMAGE_HEIGHT
        ) {
          this.errorImageMessage = this.$t('validate.image_height_size', {
            placeholder: file.name,
          })
          resolve(false)
        }
        resolve(true)
      }
    })
  }

  async submitFile(file: File) {
    try {
      const uploaded: IUploadFileResponse = await UploadApi.uploadFile(file)
      if (uploaded) {
        const imageSetting = {
          img_org: uploaded.file_url_org ?? '',
          img_thumb: uploaded.file_url_thumb ?? '',
          filename: uploaded.filename ?? '',
        }
        this.form.images = [
          {
            pc: imageSetting,
            sp: JSON.parse(JSON.stringify(imageSetting)),
          },
        ]
      }
    } catch (error) {
      console.log('error', error)
    }
  }

  async onCropImage(file) {
    this.cropperImageModalVisible = false
    this.cropFiles.push(file)
    this.loading = true
    await this.submitFile(file)
    this.loading = false
    this.onCloseModal()
  }

  onCloseModal() {
    this.cropperImageModalVisible = false
    this.errorImageMessage = ''
    this.file = null
    this.cropFiles = []
  }

  @Watch('file')
  async handleUpload() {
    if (!this.file) {
      return false
    }

    try {
      if (!(await this.validateInputFile(this.file))) {
        await this.$q
          .dialog({
            title: this.$t('messages.confirm'),
            message: this.$t('messages.image_aspect_ratio_is_not_1_1', {
              ratio: '1:1',
            }),
            cancel: {
              flat: true,
              label: this.$t('messages.no'),
            },
            ok: {
              flat: true,
              label: this.$t('messages.yes'),
            },
            persistent: true,
          })
          .onOk(async () => {
            this.cropperImageModalVisible = true
          })
          .onCancel(async () => {
            this.onCloseModal()
          })
      } else {
        this.cropFiles.push(this.file)
        this.loading = true
        await this.submitFile(this.file)
        this.loading = false
        this.file = null
        this.cropFiles = []
      }
    } catch (error) {
      console.log(error)
    }
  }

  async created() {
    this.form = { ...this.form, ...cloneDeep(this.popupData) }
    this.parentForm = this.form as any
    this.initForm = cloneDeep(this.parentForm)
  }
}
</script>

<style scoped lang="scss">
.error-message {
  width: 120px;
}
</style>
